<template>
    <section
        :class="[
            'slice',
            'slice-reinsurance--default',
            {
                'slice-reinsurance--isMobile': slice.primary.hideInMobile,
                'slice-reinsurance--default--reduce-spacing-top': slice.primary.reduceSpacing === 'top',
                'slice-reinsurance--default--reduce-spacing-bottom': slice.primary.reduceSpacing === 'bottom',
                'slice-reinsurance--default--reduce-spacing-all': slice.primary.reduceSpacing === 'all',
            },
        ]"
        aria-label="partners"
        data-testid="slice-reinsurance-default"
    >
        <div class="slice-content">
            <PrismicRichText
                v-if="slice.primary.title.length"
                :field="slice.primary.title"
                class="slice-title"
                data-testid="slice-reinsurance-title"
            />

            <div
                ref="logos"
                :class="{
                    'slice-reinsurance--default__logos': true,
                    'slice-reinsurance--default__logos--withColor': slice.primary.withColor,
                    'slice-reinsurance--default__logos--paused': !isIntersecting,
                }"
                data-testid="slice-reinsurance-logo-marquee"
            >
                <div
                    v-for="copy in LOGO_GROUP_COPIES"
                    :key="`slice-reinsurance-group-${copy}`"
                    class="slice-reinsurance--default__logos-group"
                    :aria-hidden="copy > 1 ? 'true' : undefined"
                >
                    <MktMaltPrismicImage
                        v-for="(item, i) in slice.items"
                        :key="`slice-item-${copy}-${i}`"
                        :alt="copy > 1 ? '' : (item.brandLogo.alt ?? '')"
                        :image-field="item.brandLogo"
                        sizes="mobile:100px wide:200px"
                        :class="{bigger: item.isBigger}"
                        :data-testid="copy > 1 ? undefined : `slice-reinsurance-logo--${i}`"
                    />
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {useTemplateRef} from 'vue';
    import {getSliceComponentProps, PrismicRichText} from '@prismicio/vue';
    import type {ReinsuranceSlice} from '@prismicio-types';
    import {MktMaltPrismicImage} from '#components';
    import {useIntersectionObserver} from '@malt/utils';

    defineProps(getSliceComponentProps<ReinsuranceSlice>());

    const LOGO_GROUP_COPIES = 2;

    const logos = useTemplateRef('logos');
    const {isIntersecting} = useIntersectionObserver(logos, undefined, {options: {threshold: 0}});
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .slice-reinsurance {
        &--default {
            .slice-content {
                width: 100%;
            }

            &--reduce-spacing-top .slice-content,
            &--reduce-spacing-all .slice-content {
                padding-top: 0;
            }

            &--reduce-spacing-bottom .slice-content,
            &--reduce-spacing-all .slice-content {
                padding-bottom: 0;
            }

            &__logos {
                --slice-reinsurance-logos-gap: var(--joy-core-spacing-5);
                --slice-reinsurance-marquee-duration: 20s;

                display: flex;
                flex-wrap: nowrap;
                overflow: hidden;
                width: 100%;
                filter: grayscale(100%);
                margin-top: var(--joy-core-spacing-6);
                mask-image: linear-gradient(to right, transparent 0%, currentcolor 5%, currentcolor 95%, transparent 100%);

                &--withColor {
                    filter: none;
                }

                &--paused .slice-reinsurance--default__logos-group,
                &:hover .slice-reinsurance--default__logos-group {
                    animation-play-state: paused;
                }

                @include mq.screen_md(min) {
                    --slice-reinsurance-logos-gap: var(--joy-core-spacing-12);
                    --slice-reinsurance-marquee-duration: 25s;

                    margin-top: var(--joy-core-spacing-10);
                }

                &-group {
                    display: flex;
                    flex: 0 0 auto;
                    flex-wrap: nowrap;
                    align-items: center;
                    justify-content: space-around;
                    gap: var(--slice-reinsurance-logos-gap);
                    min-width: 100%;
                    padding-right: var(--slice-reinsurance-logos-gap);
                    animation: slice-reinsurance-marquee var(--slice-reinsurance-marquee-duration) linear infinite;

                    @media (prefers-reduced-motion: reduce) {
                        animation: none;
                    }
                }

                img {
                    flex-shrink: 0;
                    max-width: 70px;
                    width: auto;
                    object-fit: contain;
                    height: 24px;

                    &.bigger {
                        height: 32px;
                    }

                    @include mq.screen_sm(min) {
                        height: 40px;
                        max-width: 100px;

                        &.bigger {
                            height: 56px;
                        }
                    }
                }
            }
        }
    }

    @keyframes slice-reinsurance-marquee {
        to {
            transform: translateX(-100%);
        }
    }
</style>
